import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { SectionCard } from "./identityForm";
import { Switch } from "./ui/switch";
import { useAuth } from "../context/AuthContext";
import { SUMMARY_NONE, usageSummary } from "../lib/settingsSummary";
import {
  USAGE_ADMIN_PARAGRAPH,
  USAGE_DISCLOSURE_PARAGRAPHS,
  USAGE_DISCLOSURE_TITLE,
  USAGE_READ_LESS,
  USAGE_READ_MORE,
  USAGE_RECORD_FIELDS,
  USAGE_RECORD_INTRO,
  USAGE_SUMMARY_LINES,
  USAGE_SWITCH_DESC,
  USAGE_SWITCH_ERROR,
  USAGE_SWITCH_LOAD_ERROR,
  USAGE_SWITCH_PARAGRAPH,
  USAGE_SWITCH_TITLE,
  USAGE_TOTALS_SENTENCE,
} from "../lib/usageDisclosure";
import { fetchUsagePreference, publish, setUsageOptOut, subscribe } from "../lib/usagePreference";

// The same row `NotificationsCard` draws (title, description, control). Copied
// rather than lifted so that card is untouched (#1109).
//
// `tapBox` (#1394, D221 G4, D357): in Settings' More (`appearance="r5"`) the row is a
// `<label>` at least 44px tall, so the whole row is the tap target for the switch and the
// 36x20 switch itself is unchanged. Every other caller keeps the plain `div`.
function Row({ title, desc, children, tapBox = false }) {
  const Tag = tapBox ? "label" : "div";
  return (
    <Tag
      className="flex items-center justify-between gap-4"
      style={tapBox ? { minHeight: "var(--nw-size-tap)", cursor: "pointer" } : undefined}
    >
      {tapBox ? (
        // Spans, not divs: a label holds phrasing content only.
        <span><span className="font-semibold text-slate-900" style={{ display: "block" }}>{title}</span><span className="text-sm text-slate-500" style={{ display: "block" }}>{desc}</span></span>
      ) : (
        <div><div className="font-semibold text-slate-900">{title}</div><div className="text-sm text-slate-500">{desc}</div></div>
      )}
      {children}
    </Tag>
  );
}

function isAdminAccount(user) {
  return user?.role === "admin" || (Array.isArray(user?.roles) && user.roles.includes("admin"));
}

/**
 * The full "what we measure" statement (#224), in Settings, directly under
 * Send feedback.
 *
 * WHY HERE. Settings is where this app already keeps its statements about
 * what leaves the device: the feedback card one card above says "Sent with
 * your name and the screen you're on" (#139), and this is the same kind of
 * sentence about the same kind of thing. It is also the only placement that
 * reaches the people already using the pilot — they have accounts, so they
 * will never see the sign-up form again, and they are exactly who the first
 * counts will be about.
 *
 * The sign-up form carries a short statement for everyone arriving after
 * this ships — that plus this card are every door into the app, so nobody
 * meets the counting without having been told about it. On the new look the
 * sign-up shows `USAGE_DISCLOSURE_ONE_LINE` and a link to the privacy notice
 * (D446, #1626); the old look still shows `USAGE_DISCLOSURE_SHORT`. Both read
 * `lib/usageDisclosure.js`.
 *
 * D205 (Andreas, 2026-10-01, #1023): this used to be THREE doors — the
 * landing page's demo buttons were the one door that skipped the sign-up
 * form, and carried the short version too. There is no public demo any
 * more (`Landing.jsx`/`FrontDoorNew.jsx` no longer import
 * `USAGE_DISCLOSURE_SHORT` at all), so there is no third door to carry it.
 *
 * IT FOLDS SINCE #456/#459, AND THAT REVERSES A DECISION RECORDED HERE.
 * This paragraph used to read: "NOT COLLAPSED behind a 'read more'. A
 * disclosure a person has to press to see is a disclosure most people do
 * not read, and the whole statement is six short paragraphs." Martin asked
 * for it to fold, on both the player's and the coach's settings page, and
 * that is a product owner's call to make. It is recorded rather than
 * deleted because the reasoning was sound and the cost is real: fewer
 * people will read this than read it yesterday.
 *
 * WHAT CARRIES THE WEIGHT NOW. `USAGE_DISCLOSURE_TITLE` is on the header,
 * visible folded, and the sign-up form still carries its short statement
 * unfolded (the one line and a privacy link on the new look, the short
 * version on the old) — so nobody meets the counting for the first time
 * behind a disclosure control.
 *
 * IT IS A SETTING AS WELL AS A STATEMENT SINCE #1109 (D218). A player or a
 * coach gets a switch under the paragraphs, and the header state is "On" or
 * "Off" read from the server (`usageSummary`). While the preference is
 * loading, or if reading it failed, the header stays `SUMMARY_NONE` and no
 * switch is drawn: a switch drawn before the server has answered would be a
 * guess, and a guess that says "off" while the server counts is the one
 * failure this card must not have. An admin sees the statement and "Admin
 * accounts are not counted." and no switch (D229).
 *
 * THE SWITCH SHOWS WHAT THE SERVER SAID, NEVER WHAT WAS TAPPED. On change it
 * disables itself, sends `PUT`, and only a successful answer moves it. A
 * failure leaves it where it was and says one line under it; no toast, and
 * nothing is claimed as changed. The Notifications switches next door are
 * inert; this one is not.
 */
export default function UsageDisclosureCard({ appearance } = {}) {
  const { user } = useAuth();
  // A plain admin session is known from the session. An admin account that
  // holds no `user_roles` row and is wearing the player hat is NOT: `/auth/me`
  // says player, so only the server's `admin` flag (`isAdminAccount`, D229,
  // read from `GET /usage/preference`) can say so (#1109 review T1).
  const sessionAdmin = isAdminAccount(user);
  const roleCounted = !sessionAdmin && (user?.role === "player" || user?.role === "coach");

  const [pref, setPref] = useState(null);
  const [readFailed, setReadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [readMore, setReadMore] = useState(false);

  const admin = sessionAdmin || pref?.admin === true;
  const counted = roleCounted && !admin;

  useEffect(() => {
    if (!roleCounted) return undefined;
    let live = true;
    const unsubscribe = subscribe((next) => {
      if (live) setPref(next);
    });
    fetchUsagePreference().then(
      (next) => {
        if (live) setPref(next);
      },
      () => {
        if (live) setReadFailed(true);
      },
    );
    return () => {
      live = false;
      unsubscribe();
    };
  }, [roleCounted]);

  const onChange = async (checked) => {
    setSaving(true);
    setSaveFailed(false);
    try {
      const next = await setUsageOptOut(!checked);
      publish(next);
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const optedOut = pref && typeof pref.opted_out === "boolean" ? pref.opted_out : null;

  // D446 (#1626): the full statement, unchanged. The old look renders it
  // straight; the new look (`appearance="r5"`, the key this card already uses
  // to tell the looks apart) folds it behind "Read more".
  const fullText = (
    <>
        <p className="text-sm text-slate-600 min-w-0">{USAGE_RECORD_INTRO}</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 min-w-0">
          {USAGE_RECORD_FIELDS.map((field) => (
            <li key={field} className="min-w-0">{field}</li>
          ))}
        </ul>
        {USAGE_DISCLOSURE_PARAGRAPHS.map((paragraph) => (
          <p key={paragraph} className="text-sm text-slate-600 min-w-0">{paragraph}</p>
        ))}
        <p className="text-sm text-slate-600 min-w-0" data-testid="settings-usage-last">
          {admin
            ? USAGE_ADMIN_PARAGRAPH
            : roleCounted && pref === null
              ? USAGE_TOTALS_SENTENCE
              : USAGE_SWITCH_PARAGRAPH}
        </p>
    </>
  );
  // D448: an admin reads the full text unfolded, as on the old look. The summary
  // lines speak to "you" as a counted person, which is untrue for an admin.
  const newLook = appearance === "r5" && !admin;

  const body = (
    <>
      {newLook ? (
        <>
          {/* The switch sentence (line 3) only when the switch row is drawn: the same condition as the full text's switch paragraph (F2). */}
          {(counted && optedOut !== null ? USAGE_SUMMARY_LINES : USAGE_SUMMARY_LINES.slice(0, 2)).map((line) => (
            <p key={line} className="text-sm text-slate-600 min-w-0" data-testid="usage-summary-line">{line}</p>
          ))}
          <button
            type="button"
            className="text-sm font-semibold underline text-left"
            style={{ minHeight: "var(--nw-size-tap)" }}
            aria-expanded={readMore}
            aria-controls="usage-full-text"
            data-testid="usage-read-more"
            onClick={() => setReadMore((v) => !v)}
          >
            {readMore ? USAGE_READ_LESS : USAGE_READ_MORE}
          </button>
          {readMore && <div id="usage-full-text" className="space-y-3 min-w-0">{fullText}</div>}
        </>
      ) : (
        fullText
      )}
      {counted && optedOut !== null && (
        <>
          <Row title={USAGE_SWITCH_TITLE} desc={USAGE_SWITCH_DESC} tapBox={appearance === "r5"}>
            <Switch
              checked={!optedOut}
              disabled={saving}
              onCheckedChange={onChange}
              aria-label={USAGE_SWITCH_TITLE}
              data-testid="settings-usage-optout"
            />
          </Row>
          {saveFailed && (
            <p className="text-sm text-slate-600 min-w-0" role="alert" data-testid="settings-usage-optout-error">
              {USAGE_SWITCH_ERROR}
            </p>
          )}
        </>
      )}
      {counted && readFailed && optedOut === null && (
        <p className="text-sm text-slate-600 min-w-0" data-testid="settings-usage-optout-load-error">
          {USAGE_SWITCH_LOAD_ERROR}
        </p>
      )}
    </>
  );

  // D400 (reverses #1135 F5): in Settings' More it is a closed fold like the
  // Player Profile fold, last before Sign out everywhere. `tapBox` makes its
  // header a 44px target there; every other caller keeps the shipped fold.
  return (
    <SectionCard
      icon={BarChart3}
      title={USAGE_DISCLOSURE_TITLE}
      collapsible
      defaultOpen={false}
      summary={counted ? usageSummary(optedOut) : SUMMARY_NONE}
      toggleTestid="settings-usage-toggle"
      usageItem="usage"
      testid="usage-disclosure"
      appearance={appearance}
      tapBox={appearance === "r5"}
    >
      {body}
    </SectionCard>
  );
}
